import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wifi, WifiOff, RefreshCw } from "lucide-react";
import { subscribeToConnection, getConnectionState, wakeBackend } from "../utils/apiService";

const ConnectionStatus = () => {
  const [state, setState] = useState(getConnectionState());
  const [dismissed, setDismissed] = useState(false);
  const [waking, setWaking] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToConnection((newState) => {
      setState(newState);
      // Reset dismissed when connection is restored
      if (newState.isConnected && !newState.isRetrying) {
        setDismissed(false);
      }
    });
    return unsubscribe;
  }, []);

  const handleWake = async () => {
    setWaking(true);
    await wakeBackend();
    setWaking(false);
    // Reload the page to refetch data
    window.location.reload();
  };

  // Don't show if connected and not retrying, or if dismissed
  if ((state.isConnected && !state.isRetrying) || dismissed) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50"
      >
        <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-4 shadow-2xl">
          {state.isRetrying ? (
            // Connecting/Retrying state
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-yellow-400 animate-spin" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">Connecting to server...</p>
                <p className="text-zinc-400 text-sm">This may take a few seconds</p>
              </div>
            </div>
          ) : (
            // Disconnected state
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <WifiOff className="w-5 h-5 text-red-400" />
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">Connection issue</p>
                  <p className="text-zinc-400 text-sm">Unable to load data from server</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleWake}
                  disabled={waking}
                  className="flex-1 px-4 py-2 bg-hsl-primary text-black font-semibold rounded-lg hover:bg-hsl-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {waking ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Reconnecting...
                    </>
                  ) : (
                    <>
                      <Wifi className="w-4 h-4" />
                      Retry Connection
                    </>
                  )}
                </button>
                <button
                  onClick={() => setDismissed(true)}
                  className="px-4 py-2 bg-zinc-800 text-zinc-400 rounded-lg hover:bg-zinc-700 transition-colors"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ConnectionStatus;
